import React, { useState, useCallback } from 'react';
import { Button, makeStyles, Tooltip, IconButton } from '@material-ui/core';
import AreYouSure from '../../Util/AreYouSure';
import { Privilege } from '../../../api';
import labelTasksService, { labelTaskInput } from '../../../services/labelTasks';
import { useHistory } from 'react-router';
import { TSession } from '../Sessions';
import { TLabelTask } from './SessionLabelTask';
import useHasPrivilege from '../../Util/useHasPrivilege';
import { useSnackbar } from 'notistack';
import buttonStyles from './Buttons/styles';
import AdminIcon from '@material-ui/icons/Healing';
import DeleteIcon from '@material-ui/icons/Delete';
import EndTaskIcon from '@material-ui/icons/LastPage';
import AddMinuteIcon from '@material-ui/icons/PlusOne';

const useStyles = makeStyles(() => ({
  permissionButton: {
    backgroundColor: "#900", 
    color: '#fff', 
    fontSize: '8px',
    margin: '2px 0',
  },
  subContainer: {
    display: 'inline',
    position: 'relative',
  },
  subMenu: {
    position: 'absolute',
    zIndex: 9,
  },
}));

type Props = {
  session: TSession;
  task: TLabelTask;
  lastLabeledFrameId: number;
  reloadTask: () => void;
};

export default function useSupervisorFunctions(props: Props) {
  const buttonClasses = buttonStyles();
  const { session, task, lastLabeledFrameId, reloadTask } = props;

  const fps: number = (session?.videos && session.videos.length > 0 && session.videos[0].fps) || 25;

  const classes = useStyles();
  let history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const hasExportPermission = useHasPrivilege(Privilege.ExportData);

  const [showSubmenu, setShowSubmenu] = useState<boolean>(false);
  const [llfAsEndDialog, setLlfAsEndDialog] = useState<boolean>(false);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);

  const gotoOverviewAndRefresh = useCallback(() => {
    history.push('/session/label/' + session.id + "/");
    window.location.reload(false);
  }, [history, session]);

  const addMinuteToEnd = useCallback(async () => {
    await labelTasksService.updateLabelTask(task.id, {
      ...labelTaskInput(task),
      endFrame: Math.round(task.endFrame + 60 * fps),
    });
    reloadTask();
    enqueueSnackbar('+ 1 minute to task end time.', { variant: 'info', autoHideDuration: 3000 });
  }, [task, fps, enqueueSnackbar, reloadTask]);

  const setLlfAsEndFrame = useCallback(async () => {
    setLlfAsEndDialog(false);
    await labelTasksService.updateLabelTask(task.id, { ...labelTaskInput(task), endFrame: lastLabeledFrameId });
    reloadTask();
  }, [task, lastLabeledFrameId, reloadTask]);

  const deleteTask = useCallback(async () => {
    setDeleteDialog(false);
    await labelTasksService.deleteLabelTask(task.id);
    gotoOverviewAndRefresh();
  }, [gotoOverviewAndRefresh, task]);

  const toggleSubmenu = useCallback(() => {
    setShowSubmenu(prev => !prev);
  }, []);

  const llfAsEndChoice: JSX.Element = (
    <AreYouSure
      title="Shorten end task."
      text="Are you sure you want to set the end of this task to the last labeled frame?"
      open={llfAsEndDialog}
      onNo={() => setLlfAsEndDialog(false)}
      onYes={setLlfAsEndFrame}
    />
  );

  const deleteChoice: JSX.Element = (
    <AreYouSure
      title="Delete task."
      text="Are you sure you want to delete this task?"
      open={deleteDialog}
      onNo={() => setDeleteDialog(false)}
      onYes={deleteTask}
    />
  );

  const sidebarButtons: JSX.Element =
    hasExportPermission ? 
    <div style={{fontSize: '10px', }}>
      <Tooltip title={'Supervisor Functions (use with care)'} arrow>
        <IconButton
          onClick={toggleSubmenu}
          className={showSubmenu ? buttonClasses.toggleIconActive : buttonClasses.toggleIcon}>
          <AdminIcon />
        </IconButton>
      </Tooltip>
      { showSubmenu &&
        <div className={classes.subContainer}>
          <div className={classes.subMenu}>
            <div>
              <Button
                variant="contained"
                className={classes.permissionButton}
                onClick={addMinuteToEnd}
              >
                <AddMinuteIcon /> Add minute
              </Button>
            </div>
            <div>
              <Button
                variant="contained"
                className={classes.permissionButton}
                onClick={() => setLlfAsEndDialog(true)}
              >
                <EndTaskIcon /> End task
              </Button>
            </div>
            <div>
              <Button
                variant="contained"
                className={classes.permissionButton}
                onClick={() => setDeleteDialog(true)}
              >
                <DeleteIcon /> Delete Task
              </Button>
            </div>
          </div>
        </div>
      }
    </div>
    : <></>;
  
  return { sidebarButtons, llfAsEndChoice, deleteChoice };
};